// frontend/packages/business-console/src/components/build-interview/FollowUpCard.jsx
//
// The conversational re-ask surface (Website Builder interview). When the
// backend saves a freeform answer but returns a scripted follow-up (the
// customer skipped an optional part the question asked for), this card shows
// that one gentle question with an "add it" box and a "just continue" out.
// The original answer is ALREADY saved; adding text resubmits the step with
// the addition appended to the saved verbatim answer (BuildInterview.jsx owns
// that merge). The backend asks at most once per step, so answering or
// declining both move forward for good.
//
// Copy rule: every string here is customer-visible. No em-dashes.

import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useTokens from '../primitives/useTokens';
import { SparkleTile } from './InterviewShell';
import AnswerField from './AnswerField';

export default function FollowUpCard({ question, busy, onAdd, onContinue }) {
  const t = useTokens();
  const [text, setText] = useState('');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box
          sx={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '.04em',
            color: t.green,
            background: t.greenBg,
            border: `1px solid ${t.greenBorder}`,
            borderRadius: '999px',
            padding: '3px 10px',
            textTransform: 'uppercase',
          }}
        >
          Saved
        </Box>
        <Typography sx={{ fontSize: 12.5, color: t.faint }}>
          One quick thing before we move on
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <SparkleTile size={34} radius={10} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 900,
              letterSpacing: '-0.4px',
              lineHeight: 1.25,
              color: t.text,
            }}
          >
            Got it. That is saved.
          </Typography>
          <Typography
            sx={{
              fontSize: 13.5,
              lineHeight: 1.6,
              color: t.textSecondary,
              mt: 0.75,
            }}
          >
            {question}
          </Typography>
        </Box>
      </Box>

      <AnswerField value={text} onChange={setText} busy={busy} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button
          size="small"
          disabled={busy}
          onClick={onContinue}
          sx={{ color: t.muted }}
        >
          No thanks, continue
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          disabled={busy || !text.trim()}
          endIcon={<ArrowForwardIcon />}
          onClick={() => onAdd(text.trim())}
          sx={{
            background: t.gradient,
            borderRadius: '10px',
            fontWeight: 800,
            px: 2.25,
            boxShadow: '0 6px 16px rgba(37,99,235,.3)',
            '&:hover': { background: t.gradient, filter: 'brightness(1.05)' },
          }}
        >
          {busy ? 'Adding it' : 'Add it'}
        </Button>
      </Box>
    </Box>
  );
}
